import { Inject, Injectable } from "@angular/core";
import { Observable, of } from 'rxjs';
import { Store } from '@ngrx/store';
import { LocationFacebookTokenPair, RequestModel, ReviewModel, SocialSharingTemplateSettings, FacebookAuthResult } from '@models/index';
import { map } from 'rxjs/operators';
import * as fromApp from '@reducers/index';
import * as fromFacebook from '@reducers/index';
import { FacebookActions } from '@actions/index';
import { SocialSharingScheduleSettings, SocialSharingScheduleType } from '@models/socialSharingScheduleSettings.model';
import { FacebookErrorModel } from '@models/facebookError.model';
import { FacebookTokenTypes } from "@enums/facebookTokenTypes.enum";

@Injectable({
  providedIn: 'root'
})
export class FacebookFacadeService {
  constructor (
    private store: Store<fromApp.State>
  ) { }

  getError$(): Observable<string> {
    return this.store.select(fromApp.getSocialSharingError);
  }

  getIsOpenModal$(): Observable<Boolean> {
    return this.store.select(fromApp.getIsSocialSharingModalOpen);
  }

  clearState$() {
    this.store.dispatch(FacebookActions.clearState());
  }

  getIsExecutingTask$(): Observable<boolean> {
    return this.store.select(fromApp.getIsSocialSharingExecuting);
  }

  // setSelectedToken$(selectedTokens: LocationFacebookTokenPair[]) {
  //   this.store.dispatch(FacebookActions.setSelectedTokens({selectedTokens}));
  // }

  sendSelectedToken$(selectedTokens: LocationFacebookTokenPair[], locationId: string) {
    this.store.dispatch(FacebookActions.sendSelectedToken({payload: {selectedTokens, locationId}}));
  }

  isOpenModal$(isOpen: Boolean) {
    this.store.dispatch(FacebookActions.openSelectPagesModal({isOpen: isOpen}));
  }

  getSelectedTokens$(): Observable<LocationFacebookTokenPair[]> {
    return this.store.select(fromApp.getSelectedFacebookTokens);
  }

  getFacebookPages$(locationId: string): Observable<LocationFacebookTokenPair[]> {
    return this.store.select(fromApp.getSocialSharingPages).pipe(
      // ensure results from a different location are not shown temporarily
      // TODO: refactor so this is not necessary
      map(tokens => !Array.isArray(tokens) ? tokens : tokens.filter(token => {
        return token.locationToken.locationId == locationId;
      }))
    );
  }

  getScheduleSettings$(locationId: string): Observable<SocialSharingScheduleSettings | undefined> {
    return this.store.select(fromApp.getSocialSharingScheduleSettings).pipe(
      // ensure results from a different location are not shown temporarily
      // TODO: refactor so this is not necessary
      map(settings => settings && settings.locationId === locationId
        ? settings
        : null
      )
    )
  }

  getTemplateSettings$(locationId: string): Observable<SocialSharingTemplateSettings | undefined> {
    return this.store.select(fromApp.getSocialSharingTemplateSettings).pipe(
      // ensure results from a different location are not shown temporarily
      // TODO: refactor so this is not necessary
      map(settings => settings && settings.locationId === locationId
        ? settings
        : null
      )
    )
  }

  getShareReviewSuccess$(): Observable<true | null> {
    return this.store.select(fromApp.getShareReviewSuccess);
  }

  getAuthState$(): Observable<boolean> {
    return this.store.select(fromApp.getFacebookAuthState);
  }

  reset() {
    this.store.dispatch(FacebookActions.reset());
  }

  dismissError() {
    this.store.dispatch(FacebookActions.error(null));
  }

  searchFacebookPages(locationId: string, TypeService: string) {
    this.store.dispatch(FacebookActions.searchFacebookPagesByLocation({ locationId, TypeService }));
  }

  enableFacebookPage(locationId: string, locationTokenId: string) {
    this.store.dispatch(FacebookActions.enableFacebookPage({ locationId, locationTokenId }));
  }

  disableFacebookPage$(locationId: string, locationTokenId: string) {
    this.store.dispatch(FacebookActions.disableFacebookPage({ locationId, locationTokenId }));
  }

  removeFacebookPage$(locationId: string, locationTokenId: string) {
    this.store.dispatch(FacebookActions.removeFacebookPage({ locationId, locationTokenId }));
  }

  searchScheduleSettings$(locationId: string) {
    this.store.dispatch(FacebookActions.searchScheduleSettings({ locationId }));
  }

  updateScheduleSettings$(locationId: string, type: SocialSharingScheduleType, minimumRating: number) {
    this.store.dispatch(FacebookActions.updateScheduleSettings({
      settings: {
        locationId,
        type,
        minimumRating
      }
    }));
  }

  searchTemplateSettings(locationId: string) {
    this.store.dispatch(FacebookActions.searchTemplateSettings({ locationId }))
  }

  updateTemplateSetting(locationId: string, encodedTemplate: string, includeStarImage: boolean) {
    this.store.dispatch(FacebookActions.updateTemplateSettings({
      settings: {
        locationId,
        encodedTemplate,
        includeStarImage
      }
    }))
  }

  authenticateWithFacebook(locationId: string , typeService: FacebookTokenTypes) {
    this.store.dispatch(FacebookActions.authenticateFacebook({ locationId: locationId, typeService: typeService }));
  }

  shareReview(
    { locationFacebookTokenPair, reviewId }: { locationFacebookTokenPair: LocationFacebookTokenPair, reviewId: string }
  ) {
    this.store.dispatch(FacebookActions.shareReview({
      locationFacebookTokenPair,
      reviewId,
    }))
  }

  setAuthState(state: boolean) {
    this.store.dispatch(FacebookActions.setAuthState({authState: state}))
  }

  clearAuthResult() {
    this.store.dispatch(FacebookActions.saveAuthToken({authResult: null}))
  }

  updateCommentOnFacebookPost(payload: { commentId: string, pageId: string, message: string, recommendationId: string, locationId: string }) {
    const request: RequestModel = {
        params: `facebook/recommendations/update-comment/${payload.commentId}?pageId=${payload.pageId}&reviewId=${payload.recommendationId}&locationId=${payload.locationId}`, 
        payload: payload.message
    }
    this.store.dispatch(FacebookActions.updateCommentOnFacebookPost({ request }))
  }

  commentFacebookPost(payload: { recommendationId: string, pageId: string, comment: string, locationId: string }) {
    const request: RequestModel = {
        params: `facebook/recommendations/post-comment?id=${payload.recommendationId}&pageId=${payload.pageId}&locationId=${payload.locationId}`, 
        payload: payload.comment
    }
    this.store.dispatch(FacebookActions.commentFacebookPost({ request }))
  }

  getCommentToBePosted$(): Observable<{ message: string, postId: string, pageId: string}> {
    return this.store.select(fromFacebook.getCommentToBePosted)
  }
  setFacebookError(error: FacebookErrorModel) {
    this.store.dispatch(FacebookActions.setFacebookError({ error }));
  }
  getFacebookError$(): Observable<FacebookErrorModel> {
    return this.store.select(fromApp.getFacebookError);
  }

}
